import 'core-js'; // 추후 polyfill없이 크로스 브라우징에 대비가 가능하다면 삭제.
import '@/common/styles/main.scss';
import 'evui/style';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import App from '@/App.vue';
import router from '@/common/router';
import { store, key } from '@/common/store';
import BaseMenu from '@/common/components/organisms/baseMenu/BaseMenu.vue';
import EVUI, { EvMessageBox, EvMessage, EvNotification } from 'evui';
import Vue3GridLayout from 'vue3-grid-layout-next';
import { vResizeObserver } from '@vueuse/components';
import { i18n } from '@/common/locale';
import { installComponent } from '@/dashboard/features/widgets/common/utils/widgetRegistry.utils';
import { registerTheme } from 'echarts/core';
import { getSafeColorListByTheme } from '@/common/utils/chartUtils';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { queryClient } from '@/common/lib/queryClient';
import { shouldShowRefreshPromptForImportError } from '@/common/utils/dynamicImportCheck.utils';
import cssHasPseudoBrowser from 'css-has-pseudo/browser';

// Chrome 98 등 :has() 미지원 브라우저용 런타임 폴백 (DSP-43853).
// PostCSS의 css-has-pseudo 변환과 함께 동작하며, 네이티브 :has() 지원 시 자동 무시된다.
cssHasPseudoBrowser(document);

const pinia = createPinia();
const app = createApp(App);

// pass the injection key
app.use(store, key);
app.use(pinia);
app.use(router);

app.use(EVUI);
app.use(i18n);
app.use(Vue3GridLayout);
app.directive('resize', vResizeObserver);
// DOMPurify 기본 설정에 data-target 명시 추가 — 챗봇이 markdown 표 안의 [name](target://...)
// anchor 를 rewrite 하여 `<a href="#" data-target="...">` 로 만든 뒤 클릭 시 라우팅하는데,
// DOMPurify 일부 버전이 알 수 없는 data-* 를 strip 하여 클릭이 동작하지 않는 문제 방어.
// 다른 v-dompurify-html 사용처는 data-target 을 쓰지 않으므로 부수영향 없음.
app.use(VueDOMPurifyHTML, {
  default: {
    ADD_ATTR: ['data-target'],
    ALLOW_DATA_ATTR: true,
  },
});
app.use(VueQueryPlugin, { queryClient });

app.config.globalProperties.$messagebox = EvMessageBox;
app.config.globalProperties.$message = EvMessage;
app.config.globalProperties.$notify = EvNotification;

app.provide('messageBox', EvMessageBox);
app.provide('message', EvMessage);
app.provide('notify', EvNotification);

installComponent(app);
app.component('BaseMenu', BaseMenu);

registerTheme('EXEM', { color: getSafeColorListByTheme('EXEM') });
registerTheme('COMPANY', { color: getSafeColorListByTheme('COMPANY') });
registerTheme('FINANCE', { color: getSafeColorListByTheme('FINANCE') });
registerTheme('MAXGAUGE', { color: getSafeColorListByTheme('MAXGAUGE') });
registerTheme('PUBLIC', { color: getSafeColorListByTheme('PUBLIC') });
registerTheme('BLUE', { color: getSafeColorListByTheme('BLUE') });
registerTheme('GREEN', { color: getSafeColorListByTheme('GREEN') });
registerTheme('BROWN', { color: getSafeColorListByTheme('BROWN') });
registerTheme('MONO', { color: getSafeColorListByTheme('MONO') });
registerTheme('PURPLE', { color: getSafeColorListByTheme('PURPLE') });

app.config.errorHandler = async (err) => {
  if (err && typeof err === 'object' && 'ignoreRequest' in err) {
    return;
  }

  const shouldShowPrompt = await shouldShowRefreshPromptForImportError(err);

  if (!shouldShowPrompt) {
    console.error(err);
    return;
  }

  EvMessageBox({
    message: i18n.global.t('MESSAGE.APP_UPDATE_DETECTED'),
    closeOnClickModal: false,
    showClose: false,
    showConfirmBtn: true,
    showCancelBtn: false,
    confirmBtnText: i18n.global.t('WORD.RELOAD'),
    onClose: (type: string) => {
      if (type === 'ok') {
        window.location.reload();
      }
    },
  });
};

if (import.meta.env.PROD) {
  console.info(
    `%c [FrontVersion] ${import.meta.env.VITE_APP_VERSION}: ${import.meta.env.VITE_COMMIT_ID}`,
    'color: green',
  );
}

app.mount('#app');
